import { Link } from "react-router-dom";
import { ArrowRight, Lock, Fingerprint, Crosshair, Zap } from "lucide-react";
import { Reveal, SectionHead, Eyebrow } from "../components/Reveal";

const VOICES = ["Buyer", "Plant leader", "Industry expert", "Distributor", "System integrator", "Procurement", "Channel"];

const VOICE_REALITIES = [
  ["Customer reality", "What buyers say, avoid, and prioritise."],
  ["Channel reality", "How routes to market actually work."],
  ["Ecosystem reality", "Where influence, friction, and trust sit."],
];

const RED_TEAM = [
  "Product–market fit", "Pricing", "Competition", "Distribution", "Regulation",
  "Localisation", "Sales cycle", "Partner dependence", "Service requirements",
  "Capital needs", "Incumbent resistance",
];

const ROUTES = ["Distributor", "Strategic partner", "Joint venture", "Subsidiary", "Pilot → Partner → Scale"];
const CRITERIA = ["Speed", "Investment", "Control", "Risk", "Evidence"];

const EVIDENCE_GATES = [
  ["Evidence gate — clear", "Threshold met.", "Enter now — evidence clears the threshold."],
  ["Evidence gate — partial", "Key unknowns remain.", "Validate first — critical unknowns remain."],
  ["Evidence gate — failed", "Threshold not met.", "Do not enter yet — risk outweighs opportunity."],
];

const MEETING_STEPS = [
  "Account rationale & priority score",
  "Target buyer & decision-maker hypothesis",
  "Trigger event & timing",
  "Recommended conversation angle",
  "Potential objection & response",
  "Recommended next step",
];

const PHASES = [
  { days: "01–30", name: "Align", desc: "Confirm thesis, owners, and evidence gaps." },
  { days: "31–60", name: "Validate", desc: "Run priority conversations and partner tests." },
  { days: "61–90", name: "Activate", desc: "Commit resources, sequence moves, and measure signal." },
];

const DAY90 = ["Go", "Scale", "Pause", "Reposition"];

const ENGAGEMENTS = [
  { tier: "Tier I", name: "Essential Intelligence", price: "from US$3,500", desc: "Fast, focused answers to specific market questions.", flagship: false },
  { tier: "Tier II", name: "ENEXUR India Market Intelligence™ Flagship", price: "US$7,500", desc: "Comprehensive country and sector intelligence for high-confidence decisions.", flagship: true },
  { tier: "Tier III", name: "Strategic Entry Intelligence", price: "from US$15,000", desc: "Build your entry thesis, assess risk, and define the optimal route-to-market.", flagship: false },
  { tier: "Tier IV", name: "Market Entry Programme", price: "US$25,000–50,000+", desc: "Sustained partnership for activation, execution, and measurable outcomes.", flagship: false },
];

const ModuleLabel = ({ children }) => (
  <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">{children}</p>
);

const Chip = ({ children }) => (
  <span className="border border-gold/20 bg-abyss/60 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-ivory/80 transition-colors duration-200 hover:border-gold/50 hover:text-gold">
    {children}
  </span>
);

export const DecisionSupportSection = () => (
  <section id="in-action" className="py-24 md:py-32" data-testid="section-in-action">
    <div className="wrap">
      <SectionHead
        index="07 — Decision support in action"
        title={<>From evidence to movement.</>}
        lead="The intelligence is built to be used — in the boardroom, in the negotiation, and in the first conversations in market."
      />

      <div className="mt-20 space-y-20 md:space-y-28">
        {/* Voice of India */}
        <div className="grid gap-10 lg:grid-cols-12">
          <Reveal className="lg:col-span-4">
            <ModuleLabel>Voice of India™</ModuleLabel>
            <h3 className="mt-5 font-display text-2xl leading-snug text-ivory md:text-3xl">
              Structured listening, at field level.
            </h3>
            <p className="mt-4 text-[15px] leading-relaxed text-mist">
              For selected engagements, ENEXUR conducts structured primary research
              with the people who shape the market:
            </p>
            <p className="mt-8 border-t border-gold/15 pt-6">
              <span className="font-display text-5xl text-gold-soft">15–25</span>
              <span className="mt-2 block text-sm text-mist">
                structured market conversations, depending on engagement scope.
              </span>
            </p>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-8">
            <div className="flex h-full flex-wrap content-start items-start gap-3 border border-gold/15 bg-abyss/40 p-8 md:p-10">
              {VOICES.map((v) => (
                <Chip key={v}>{v}</Chip>
              ))}
              <div className="mt-6 w-full">
                {VOICE_REALITIES.map(([t, d]) => (
                  <div key={t} className="flex flex-col gap-1 border-t border-gold/10 py-3 sm:flex-row sm:items-baseline sm:gap-6">
                    <span className="w-56 shrink-0 font-mono text-[10px] uppercase tracking-[0.24em] text-gold">
                      {t}
                    </span>
                    <span className="text-sm text-mist">{d}</span>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>

        {/* Red Team India */}
        <div className="grid gap-10 lg:grid-cols-12">
          <Reveal className="order-2 lg:order-1 lg:col-span-8">
            <div className="flex h-full flex-wrap content-start gap-3 border border-gold/15 bg-abyss/40 p-8 md:p-10">
              {RED_TEAM.map((r) => (
                <Chip key={r}>{r}</Chip>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.1} className="order-1 lg:order-2 lg:col-span-4">
            <ModuleLabel>Red Team India™</ModuleLabel>
            <h3 className="mt-5 font-display text-2xl leading-snug text-ivory md:text-3xl">
              Before the market tests your thesis, we test it first.
            </h3>
            <p className="mt-4 text-[15px] leading-relaxed text-mist">
              ENEXUR deliberately challenges the proposed entry strategy instead of
              simply validating assumptions.
            </p>
          </Reveal>
        </div>

        {/* Decision Simulator */}
        <Reveal>
          <div className="border border-gold/15 bg-abyss/40 p-8 md:p-12">
            <ModuleLabel>India Entry Decision Simulator™</ModuleLabel>
            <h3 className="mt-5 max-w-2xl font-display text-2xl leading-snug text-ivory md:text-3xl">
              A structured way to see the consequences of each path.
            </h3>
            <div className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
              {ROUTES.map((r, i) => (
                <div
                  key={r}
                  className="group border border-gold/20 bg-ink p-5 transition-all duration-300 hover:border-gold/50"
                >
                  <span className="font-mono text-[10px] text-gold/60">
                    Route {String(i + 1).padStart(2, "0")}
                  </span>
                  <p className="mt-3 font-display text-lg leading-snug text-ivory">{r}</p>
                </div>
              ))}
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 border-t border-gold/12 pt-6">
              <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-mist/60">
                Evaluated against
              </span>
              {CRITERIA.map((c) => (
                <span key={c} className="font-mono text-[11px] uppercase tracking-[0.2em] text-gold">
                  {c}
                </span>
              ))}
            </div>
            <div className="mt-8 grid gap-px border border-gold/15 bg-gold/15 md:grid-cols-3">
              {EVIDENCE_GATES.map(([gate, cond, outcome]) => (
                <div key={gate} className="bg-ink p-6">
                  <p className="font-mono text-[9px] uppercase tracking-[0.24em] text-gold/70">{gate}</p>
                  <p className="mt-3 text-sm text-mist">{cond}</p>
                  <p className="mt-3 font-display text-lg leading-snug text-ivory">{outcome}</p>
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        {/* First 10 Meetings Pack */}
        <Reveal>
          <div className="border border-gold/15 bg-abyss/40 p-8 md:p-12">
            <ModuleLabel>First 10 Meetings Pack</ModuleLabel>
            <h3 className="mt-5 max-w-2xl font-display text-2xl leading-snug text-ivory md:text-3xl">
              The first ten meetings should reduce uncertainty.
            </h3>
            <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-6">
              {MEETING_STEPS.map((s, i) => (
                <div key={s} className="relative">
                  <span className="font-mono text-[10px] tracking-[0.24em] text-gold">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <p className="mt-3 border-t border-gold/20 pt-3 text-sm leading-snug text-ivory/85">
                    {s}
                  </p>
                  {i < MEETING_STEPS.length - 1 && (
                    <ArrowRight
                      size={13}
                      className="absolute -right-3 top-1 hidden text-gold/50 lg:block"
                      aria-hidden="true"
                    />
                  )}
                </div>
              ))}
            </div>
            <p className="mt-8 text-sm italic text-mist/75">
              A prioritised first-contact list designed for immediate commercial action.
            </p>
          </div>
        </Reveal>

        {/* 90-Day Blueprint */}
        <Reveal>
          <div className="border border-gold/15 bg-abyss/40 p-8 md:p-12">
            <ModuleLabel>90-Day Activation Blueprint</ModuleLabel>
            <h3 className="mt-5 max-w-2xl font-display text-2xl leading-snug text-ivory md:text-3xl">
              From evidence to motion in ninety days.
            </h3>
            <div className="mt-10 grid gap-px border border-gold/15 bg-gold/15 sm:grid-cols-3">
              {PHASES.map((p, i) => (
                <div key={p.name} className="bg-ink p-8">
                  <span className="font-display text-3xl text-gold-soft">{p.days}</span>
                  <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.28em] text-ivory/85">
                    Days {p.days} — {p.name}
                  </p>
                  <p className="mt-3 text-sm leading-relaxed text-mist">{p.desc}</p>
                  <div className="mt-6 h-0.5 w-full bg-gold/10">
                    <div
                      className="h-full bg-gold/60"
                      style={{ width: `${(i + 1) * 33.3}%` }}
                      aria-hidden="true"
                    />
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 border-t border-gold/12 pt-6">
              <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-mist/60">
                Day 90 decision
              </span>
              {DAY90.map((d) => (
                <span key={d} className="font-mono text-[11px] uppercase tracking-[0.2em] text-gold">
                  {d}
                </span>
              ))}
            </div>
          </div>
        </Reveal>
      </div>

      <Reveal delay={0.1}>
        <p className="mt-14 max-w-2xl border-l-2 border-gold/50 pl-5 text-sm italic leading-relaxed text-mist">
          The recommended path depends on your evidence, product category,
          regulatory gates, and commercial economics.
        </p>
      </Reveal>
    </div>
  </section>
);

export const EngagementsSection = () => (
  <section id="engagements" className="py-24 md:py-32" data-testid="section-engagements">
    <div className="wrap">
      <SectionHead
        index="08 — Engagements"
        title={<>Choose the depth of intelligence.</>}
        lead="Four levels of engagement, from a focused evidence sprint to a sustained market-entry programme."
      />

      <div className="mt-16 grid gap-6 md:mt-20 md:grid-cols-2 lg:grid-cols-4">
        {ENGAGEMENTS.map((e, i) => (
          <Reveal key={e.name} delay={i * 0.1} className="h-full">
            <div
              className={`relative flex h-full flex-col p-8 transition-all duration-300 ${
                e.flagship
                  ? "border border-gold bg-hull2/70 shadow-[0_0_60px_rgba(201,162,75,0.1)] lg:-translate-y-2"
                  : "border border-mist/15 bg-abyss/50 hover:border-gold/40"
              }`}
              data-testid={`engagement-tier-${i + 1}`}
            >
              {e.flagship && (
                <span className="absolute -top-3 left-6 bg-gold px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.18em] text-ink">
                  Principal executive-decision product
                </span>
              )}
              <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-mist/60">
                {e.tier}
              </span>
              <h3 className="mt-6 font-display text-xl leading-snug text-ivory">{e.name}</h3>
              <p className="mt-3 text-sm leading-relaxed text-mist">{e.desc}</p>
              <p
                className={`mt-auto pt-10 font-display text-2xl ${
                  e.flagship ? "text-gold-soft" : "text-gold"
                }`}
              >
                {e.price}
              </p>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.15}>
        <p className="mt-10 font-mono text-[10px] uppercase tracking-[0.22em] text-mist/60">
          Scope and final engagement terms are confirmed with ENEXUR.
        </p>
        <div className="mt-12">
          <Link to="/client-intelligence-brief" className="btn-gold" data-testid="engagements-cta">
            Start Your Client Intelligence Brief
          </Link>
        </div>
      </Reveal>
    </div>
  </section>
);

export const WhyEnexurSection = () => (
  <section
    id="why-enexur"
    className="border-y border-gold/15 bg-abyss py-24 md:py-36"
    data-testid="section-why-enexur"
  >
    <div className="wrap">
      <Reveal className="text-center">
        <p className="inline-flex items-center justify-center gap-3 font-mono text-[11px] uppercase tracking-[0.34em] text-gold">
          <span className="inline-block h-px w-8 bg-gold/60" aria-hidden="true" />
          09 — Why ENEXUR
          <span className="inline-block h-px w-8 bg-gold/60" aria-hidden="true" />
        </p>
        <h2 className="mx-auto mt-10 max-w-4xl font-display text-4xl leading-[1.1] text-ivory md:text-6xl">
          We sit between{" "}
          <em className="font-light text-gold-soft">intelligence and revenue.</em>
        </h2>
      </Reveal>

      <Reveal delay={0.1}>
        <div className="mx-auto mt-20 max-w-3xl">
          {[
            ["Research", "describes"],
            ["Consulting", "advises"],
            ["Agencies", "execute"],
          ].map(([who, verb]) => (
            <div
              key={who}
              className="flex items-baseline justify-between border-b border-mist/10 py-5"
            >
              <span className="font-display text-xl text-mist md:text-2xl">{who}</span>
              <span className="font-mono text-[11px] uppercase tracking-[0.26em] text-mist/70">
                {verb}
              </span>
            </div>
          ))}
          <div className="mt-10 border border-gold/40 bg-hull2/50 p-8 md:p-10">
            <div className="flex flex-wrap items-baseline justify-between gap-4">
              <span className="font-display text-2xl text-ivory md:text-3xl">ENEXUR</span>
              <span className="font-mono text-[11px] uppercase tracking-[0.26em] text-gold">
                connects
              </span>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-x-4 gap-y-3">
              {["Intelligence", "Decision", "Market Access", "Revenue"].map((node, i) => (
                <span key={node} className="flex items-center gap-4">
                  <span className="font-display text-lg text-gold-soft md:text-xl">{node}</span>
                  {i < 3 && <ArrowRight size={14} className="text-gold/60" aria-hidden="true" />}
                </span>
              ))}
            </div>
          </div>
        </div>
      </Reveal>

      <Reveal delay={0.15}>
        <div className="mt-16 flex flex-wrap justify-center gap-4">
          {[
            [Lock, "Private by design"],
            [Fingerprint, "Bespoke to the decision"],
            [Crosshair, "Evidence-led"],
            [Zap, "Built for action"],
          ].map(([Icon, label]) => (
            <span
              key={label}
              className="flex items-center gap-3 border border-gold/25 px-6 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-ivory/85 transition-colors duration-200 hover:border-gold/60 hover:text-gold"
            >
              <Icon size={14} className="text-gold" aria-hidden="true" />
              {label}
            </span>
          ))}
        </div>
      </Reveal>
    </div>
  </section>
);

export const FinalCtaSection = () => (
  <section className="relative overflow-hidden py-28 md:py-40" data-testid="section-final-cta">
    <div
      className="pointer-events-none absolute inset-0"
      aria-hidden="true"
      style={{
        background:
          "radial-gradient(900px 500px at 50% 100%, rgba(201,162,75,0.08), transparent 65%)",
      }}
    />
    <div className="wrap relative text-center">
      <Reveal>
        <Eyebrow className="justify-center">Before you commit</Eyebrow>
        <h2 className="mx-auto mt-10 max-w-4xl font-display text-3xl leading-[1.15] text-ivory md:text-5xl">
          Before you commit capital to India, know where the opportunity is, who
          will buy, who can help you enter, what could go wrong, and{" "}
          <em className="font-light text-gold-soft">what to do next.</em>
        </h2>
        <div className="mt-14">
          <Link
            to="/client-intelligence-brief"
            className="btn-gold"
            data-testid="final-cta"
          >
            Apply for an ENEXUR India Intelligence Engagement
          </Link>
        </div>
        <p className="mt-8 font-mono text-[10px] uppercase tracking-[0.26em] text-mist/60">
          Or write to{" "}
          <a
            href="mailto:business@enexur.in"
            data-testid="final-cta-email"
            className="text-gold transition-colors hover:text-gold-soft"
          >
            business@enexur.in
          </a>
        </p>
      </Reveal>
    </div>
  </section>
);
